import React, { FC, useState, useEffect, useCallback } from "react";
import { FormSpy } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import client from "v3/services/graphql";
import { FieldInput } from "@pepdirect/v3/ui/FieldInput";
import { FieldSelect } from "@pepdirect/v3/ui/FieldSelect";
import { FieldCheckbox } from "@pepdirect/v3/ui/FieldCheckbox";
import { AutocompleteInput } from "@pepdirect/v3/ui/AutocompleteInput";
import { EmailOptIn } from "@pepdirect/v3/ui/EmailOptIn";
import {
  useFindAddressLazyQuery,
  useRetrieveAddressLazyQuery,
  ContactInfo,
  LocalizationQuery,
} from "v3/services/graphql/generated";
// this is v2 because we are only using one tenant query
// and it needs to be v2 in getInitialProps where it is initially fetched
// once v2 is no longer used then this can be on v3
import { TenantQuery } from "services/graphql/generated";
import {
  getCountries,
  getCountryDropdownOption,
  getStates,
} from "v3/helpers/address";
import {
  AutocompletedAddressValues,
  ReactSelectDropdownOption,
  SelectedItemProps,
} from "@pepdirect/v3/types";
import { usePhoneNumberCountryCodeOnLoad } from "v3/hooks/usePhoneNumberCountryCodeOnLoad";
import { addressFields, emptyOption } from "v3/constants/address";
import {
  StyledAutocompleteWrapper,
  StyledCountry,
  StyledManualEnterButton,
  StyledManualEnterFields,
  StyledRegion,
  StyledRequiredFields,
  StyledState,
  StyledZip,
  StyledStateZipCountry,
  StyledWrapper,
} from "./index.styled";

const formId = "shippingAddress";

export interface NewShippingAddressFormProps {
  showIsDefault?: boolean;
  countryOnLoad?: string | null;
  enteredContactInfo?: ContactInfo | null;
  handleRetrieveAddressError: (e: unknown) => void;
  localization: LocalizationQuery["localization"] | null;
  setShipSelectedFromAutocomplete: (
    ship: AutocompletedAddressValues | null
  ) => void;
  shipSelectedFromAutocomplete: AutocompletedAddressValues | null;
  showCompany?: boolean | null;
  showEmail?: boolean;
  showEmailOptIn?: boolean;
  tenant: TenantQuery["tenant"];
  showTaxId?: boolean | null;
  showAddressFieldsInitially?: boolean;
}

// This is the AddressDetailsFormB from v2, but only for shipping address
// as there is much more functionality here than with billing address
export const NewShippingAddressForm: FC<NewShippingAddressFormProps> = ({
  showIsDefault = false,
  countryOnLoad,
  enteredContactInfo,
  handleRetrieveAddressError,
  localization,
  shipSelectedFromAutocomplete,
  setShipSelectedFromAutocomplete,
  showAddressFieldsInitially = false,
  showCompany = false,
  showEmail = false,
  showEmailOptIn = false,
  tenant,
  showTaxId = false,
}) => {
  const {
    requiredFieldsText = "Required fields",
    regionText = "Region",
    firstNameLabel = "First Name",
    lastNameLabel = "Last Name",
    emailLabel = "Email",
    line1Label = "Street Address",
    line2Label = "Apt., Suite, etc. (Optional)",
    cityLabel = "City",
    stateLabel = "State",
    zipLabel = "Zip Code",
    countryLabel = "Country",
    phoneLabel = "Phone",
    companyLabel = "Company",
    isDefaultLabel = "Make this my default shipping address",
    taxIdLabel = "Tax ID",
    manuallyEnterAddressText = "Manually enter my address",
    autocompletePlaceholderText = "Start typing address...",
  } = localization?.forms?.address || {};

  const nicknameLabel = "Address Nickname (Optional)";
  const specialInstructionsLabel = "Special Delivery Instructions (Optional)";

  const [autocompletedState, setAutocompletedState] =
    useState<ReactSelectDropdownOption>();

  const [stateOptions, setStateOptions] = useState<ReactSelectDropdownOption[]>(
    []
  );
  const [selectedCountry, setSelectedCountry] =
    useState<ReactSelectDropdownOption>(emptyOption);
  const [showAddressFields, setShowAddressFields] = useState(
    showAddressFieldsInitially
  );

  const [findAddress] = useFindAddressLazyQuery({ client });
  const [retrieveAddress] = useRetrieveAddressLazyQuery({ client });

  const showAddressNickname =
    !!tenant?.contactConfig.shippingAddressConfig.addressNickname;
  const showSpecialInstructions =
    !!tenant?.contactConfig.shippingAddressConfig.addressSpecialInstructions;

  const { countriesAllowed = [], statesConfig = [] } =
    tenant?.contactConfig?.shippingAddressConfig || {};

  const isStateRequired =
    !!tenant?.contactConfig?.shippingAddressConfig?.isStateRequired;
  const showCountryDropdown = countriesAllowed.length > 1;

  const { code: countryCode } = usePhoneNumberCountryCodeOnLoad(tenant);

  // https://kishoreconnect.com/javascript-and-google-chrome-autofill
  /* select an input element autofilled by browser */
  const autofilledInput = document.querySelector(`input:-webkit-autofill`);
  /* check if the input field is autofilled - not null if autofilled */
  if (autofilledInput && !showAddressFields) {
    setShowAddressFields(true);
  }

  const getAndSetStates = useCallback(
    (countryOption: ReactSelectDropdownOption) => {
      if (countryOption !== null) {
        const statesInSelectedCountry = getStates(
          statesConfig,
          undefined,
          countryOption
        );
        setStateOptions(statesInSelectedCountry);
      }
    },
    [statesConfig]
  );

  useEffect(() => {
    if (countryOnLoad) {
      const countryOption = getCountryDropdownOption(
        countryOnLoad,
        countriesAllowed
      );
      if (countryOption) getAndSetStates(countryOption);
    }
  }, [countryOnLoad, countriesAllowed, getAndSetStates, tenant]);

  // expand form if fields under the expansion have values
  useEffect(() => {
    const { shippingAddress } = enteredContactInfo || {};
    if (!shippingAddress?.addressId) {
      const { line2, city, state, zip, countryCode } = shippingAddress || {};
      if (line2 || city || state || zip || countryCode)
        setShowAddressFields(true);
    }
  }, [enteredContactInfo]);

  useEffect(() => {
    if (shipSelectedFromAutocomplete) {
      setShowAddressFields(true);
      const countries = getCountries(countriesAllowed);
      const selectedCountry =
        countries &&
        countries.find(
          (co) => co.value === shipSelectedFromAutocomplete.country
        );

      if (selectedCountry) setSelectedCountry(selectedCountry);
    }
  }, [shipSelectedFromAutocomplete, countriesAllowed]);

  useEffect(() => {
    if (shipSelectedFromAutocomplete && stateOptions && selectedCountry) {
      const state = stateOptions.find(
        (st) => st.value === shipSelectedFromAutocomplete.state
      );

      if (state) setAutocompletedState(state);
    }
  }, [
    shipSelectedFromAutocomplete,
    statesConfig,
    selectedCountry,
    stateOptions,
  ]);

  const handleFindAddresses = async (
    inputValue?: string,
    container?: string
  ): Promise<SelectedItemProps[]> => {
    try {
      if (!inputValue && !container) return [];

      const { data, error } = await findAddress({
        variables: { input: { text: inputValue || "", container } },
      });
      if (error) throw error;

      const addresses = data?.findAddress?.items || [];
      const foundAddresses: SelectedItemProps[] = [];

      addresses.forEach((address) => {
        const { id, value, type, description } = address;

        foundAddresses.push({
          id: id || "",
          value: value || "",
          type: type || "",
          label: `${value} ${description}`.trim(),
        });
      });

      return foundAddresses;
    } catch (e) {
      // form should fallback to regular form on findAddress errors
      // don't show any error to user
      console.warn(e);
    }

    return [];
  };

  const handleRetrieveAddresses = async (
    addressId: string
  ): Promise<AutocompletedAddressValues[]> => {
    try {
      const { data, error } = await retrieveAddress({
        variables: { id: addressId },
      });
      if (error) throw error;

      const addresses = data?.retrieveAddress?.items || [];
      const retrievedAddresses: AutocompletedAddressValues[] = [];

      addresses.forEach((address) => {
        const { line1, subBuilding, city, state, zip, country } = address;

        retrievedAddresses.push({
          line1: line1 || "",
          line2: subBuilding || "",
          city: city || "",
          state: state || "",
          zip: zip || "",
          country: country || "",
        });
      });

      return retrievedAddresses;
    } catch (e) {
      handleRetrieveAddressError(e);
    }

    return [];
  };

  const handleSelectedAddress = (addr: AutocompletedAddressValues | null) => {
    setShipSelectedFromAutocomplete(addr);
  };

  const updateStatesOnCountryChange = (
    countryOption: ReactSelectDropdownOption
  ) => {
    getAndSetStates(countryOption);
  };

  return (
    <StyledWrapper>
      {/*
      https://final-form.org/docs/react-final-form/examples ---> jump to Declarative Form Rules
      Update stateOptions when selectedCountry changes, without setting state in
      render, because that is bad and causes the warning `Cannot update a
      component while rendering a different component`
      */}
      <FormSpy subscription={{}}>
        {() => (
          <OnChange name={`${formId}.${addressFields.country.name}`}>
            {updateStatesOnCountryChange}
          </OnChange>
        )}
      </FormSpy>

      <StyledRequiredFields>{`${requiredFieldsText} *`}</StyledRequiredFields>
      {!showCountryDropdown && (
        <StyledRegion>
          {regionText} - {countriesAllowed[0].name}
        </StyledRegion>
      )}

      <FieldInput
        autoComplete={addressFields.firstName.autoComplete}
        name={`${formId}.${addressFields.firstName.name}`}
        label={`${firstNameLabel}*`}
      />

      <FieldInput
        autoComplete={addressFields.lastName.autoComplete}
        name={`${formId}.${addressFields.lastName.name}`}
        label={`${lastNameLabel}*`}
      />

      {showCompany && (
        <FieldInput
          autoComplete={addressFields.company.autoComplete}
          name={`${formId}.${addressFields.company.name}`}
          label={companyLabel}
        />
      )}

      {showTaxId && (
        <FieldInput
          name={`${formId}.${addressFields.taxId.name}`}
          label={taxIdLabel}
        />
      )}

      {showEmail && (
        <FieldInput
          autoComplete={addressFields.email.autoComplete}
          name={`${formId}.${addressFields.email.name}`}
          label={`${emailLabel}*`}
        />
      )}

      {showEmailOptIn && (
        <FieldCheckbox
          name={`${formId}.${addressFields.emailOptIn.name}`}
          label={
            <EmailOptIn
              customText={tenant?.brandConfig.customTexts || []}
              location="checkout"
            />
          }
        />
      )}
      <FieldInput
        name={`${formId}.${addressFields.phone.name}`}
        label={`${phoneLabel}*`}
        autoComplete="tel-national"
        inlineInputText={countryCode}
      />
      <StyledAutocompleteWrapper>
        <AutocompleteInput
          autoComplete={addressFields.line1.autoComplete}
          name={`${formId}.${addressFields.line1.name}`}
          label={`${line1Label}*`}
          placeholder={autocompletePlaceholderText}
          setAutocompletedAddressValues={handleSelectedAddress}
          findAddresses={handleFindAddresses}
          retrieveAddresses={handleRetrieveAddresses}
        />
      </StyledAutocompleteWrapper>

      {!showAddressFields && (
        <StyledManualEnterButton
          type="text"
          onClick={() => setShowAddressFields(true)}
        >
          {manuallyEnterAddressText}
        </StyledManualEnterButton>
      )}

      <StyledManualEnterFields show={showAddressFields}>
        <FieldInput
          autoComplete={addressFields.line2.autoComplete}
          name={`${formId}.${addressFields.line2.name}`}
          label={line2Label}
          autocompleteValue={shipSelectedFromAutocomplete?.line2}
        />

        <FieldInput
          autoComplete={addressFields.city.autoComplete}
          name={`${formId}.${addressFields.city.name}`}
          label={`${cityLabel}*`}
          autocompleteValue={shipSelectedFromAutocomplete?.city}
        />

        <StyledStateZipCountry>
          {isStateRequired && (
            <StyledState>
              <FieldSelect
                autoComplete={addressFields.state.autoComplete}
                name={`${formId}.${addressFields.state.name}`}
                label={`${stateLabel}*`}
                options={stateOptions}
                autocompleteValue={autocompletedState}
              />
            </StyledState>
          )}
          <StyledZip>
            <FieldInput
              autoComplete={addressFields.zip.autoComplete}
              name={`${formId}.${addressFields.zip.name}`}
              label={`${zipLabel}*`}
              autocompleteValue={shipSelectedFromAutocomplete?.zip}
            />
          </StyledZip>

          <StyledCountry show={showCountryDropdown}>
            <FieldSelect
              autoComplete={addressFields.country.autoComplete}
              name={`${formId}.${addressFields.country.name}`}
              label={`${countryLabel}*`}
              options={getCountries(countriesAllowed)}
              autocompleteValue={
                shipSelectedFromAutocomplete ? selectedCountry : undefined
              }
            />
          </StyledCountry>
        </StyledStateZipCountry>
      </StyledManualEnterFields>

      {showAddressNickname && (
        <FieldInput
          label={nicknameLabel}
          name={`${formId}.${addressFields.nickname.name}`}
        />
      )}
      {showSpecialInstructions && (
        <FieldInput
          label={specialInstructionsLabel}
          name={`${formId}.${addressFields.specialInstructions.name}`}
        />
      )}

      {showIsDefault && (
        <FieldCheckbox
          label={isDefaultLabel}
          name={`${formId}.${addressFields.isDefault.name}`}
        />
      )}
    </StyledWrapper>
  );
};
